// The activity log's AI review tab (console prototype 82-governance.js aiReview(), approved 1 Nov 2026).
// The AI only reads the log. Flags and the daily summary come from staff_ai_review (written by the scheduled
// staff-log-review function); a question goes to the staff-log-ask function, which turns it into a filter
// (never SQL) that staff_activity runs. A suggestion runs only when a super admin approves it with the
// 2-step code and a reason (staff_ai_flag_decide); the log then says "AI suggested, name approved".
// Server contract: docs/STAFF_GOVERNANCE_SCHEMA.md §1.4.
import { useCallback, useEffect, useState } from 'react';
import { plain } from '../lib/errors';
import { L, prefs } from '../lib/prefs';
import { rpc, sb } from '../lib/supabase';
import { Btn, Card, Empty, Ic } from '../ui/kit';
import { confirmBox } from '../ui/modal';
import { guarded } from '../ui/guard';
import { Pill } from '../ui/table';
import { toast } from '../ui/toast';
import type { Entry, Filter } from './ActivityLog';

export type Flag = { id: string; severity: 'high' | 'med' | 'low'; title_en: string; title_ar: string; why_en: string; why_ar: string; filter: Filter; action: { label_en: string; label_ar: string } | null };
export type Review = { digest: { lines_en: string[]; lines_ar: string[] }; flags: Flag[]; can_approve: boolean };
const SEV = (): Record<Flag['severity'], [string, string]> => ({ high: [L('High', 'مرتفع'), 'bad'], med: [L('Medium', 'متوسط'), 'warn'], low: [L('Low', 'منخفض'), 'info'] });
const ar = () => prefs.get().lang === 'ar';

export function useReview() {
  const [r, setR] = useState<Review | null | 'failed'>(null);
  const reload = useCallback(async () => { try { setR(await rpc<Review>('staff_ai_review')); } catch { setR('failed'); } }, []);
  useEffect(() => { void reload(); }, [reload]);
  return [r, reload] as const;
}

export function AiReview({ r, reload, show, what, when }: { r: Review; reload: () => Promise<void>; show: (f: Filter) => void; what: (e: Entry) => string; when: (t: string) => string }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [ans, setAns] = useState<{ rows: Entry[] } | null>(null);
  const ask = async (question: string) => {
    if (!question.trim()) return;
    setQ(question); setBusy(true);
    try {
      const { data, error } = await sb.functions.invoke('staff-log-ask', { body: { question: question.trim(), lang: prefs.get().lang } });
      if (error) throw error;
      const page = await rpc<{ rows: Entry[] }>('staff_activity', { p_filter: (data as { filter: Filter }).filter, p_before_seq: null, p_limit: 6 });
      setAns({ rows: page.rows });
    } catch (e) { toast(plain(e), { tone: 'bad' }); }
    setBusy(false);
  };
  const approve = (x: Flag) => {
    const label = ar() ? x.action!.label_ar : x.action!.label_en;
    confirmBox({
      title: label + '?', ok: L('Approve', 'اعتماد'),
      body: L('The AI suggested this. It runs only because you confirm it, and the log will say "AI suggested, you approved".', 'اقترح الذكاء هذا. يُنفذ فقط بتأكيدك ويُسجل كذلك.'),
      onOk: () => guarded(label, async reason => { await rpc('staff_ai_flag_decide', { p_id: x.id, p_decision: 'approve', p_reason: reason }); toast(L('Done', 'تم')); await reload(); }),
    });
  };
  const dismiss = async (x: Flag) => {
    try { await rpc('staff_ai_flag_decide', { p_id: x.id, p_decision: 'dismiss', p_reason: null }); await reload(); } catch (e) { toast(plain(e), { tone: 'bad' }); }
  };
  const sugg = [L('Who changed a plan this week?', 'من غيّر خطة هذا الأسبوع؟'), L('Failed sign-ins today', 'محاولات دخول فاشلة اليوم'), L('Who exported anything today?', 'من صدّر شيئاً اليوم؟')];
  return (
    <>
      <div className="grid g-12">
        <Card cls="span-5" title={<><Ic n="spark" /> {L('Today in short', 'اليوم باختصار')}</>}>
          <ul className="ai-sum">{(ar() ? r.digest.lines_ar : r.digest.lines_en).map((s, i) => <li key={i}>{s}</li>)}</ul>
        </Card>
        <Card cls="span-7" title={<><Ic n="spark" /> {L('Ask about the log', 'اسأل عن السجل')}</>}>
          <form className="lg-ask" onSubmit={e => { e.preventDefault(); void ask(q); }}>
            <input className="inp" id="lg-q" placeholder={L('e.g. who exported anything today?', 'مثال: من صدّر شيئاً اليوم؟')} aria-label={L('Ask about the log', 'اسأل عن السجل')} value={q} onChange={e => setQ(e.target.value)} />
            <Btn kind="primary" icon="send" sm busy={busy} type="submit">{L('Ask', 'اسأل')}</Btn>
          </form>
          <div className="lg-sugg">{sugg.map(s => <button key={s} type="button" className="chip" onClick={() => void ask(s)}>{s}</button>)}</div>
          {ans ? <div className="lg-ans">{ans.rows.length
            ? <><p>{L(`${ans.rows.length} matching ${ans.rows.length === 1 ? 'entry' : 'entries'}:`, `${ans.rows.length} سجلاً مطابقاً:`)}</p><ul className="lg-ans-l">{ans.rows.map(e => <li key={e.seq}><span className="mono-n">{when(e.at)}</span> <b><bdi>{e.staff_name || L('Unknown', 'مجهول')}</bdi></b> {what(e)}</li>)}</ul></>
            : <p>{L('Nothing in the log matches that.', 'لا شيء في السجل يطابق ذلك.')}</p>}</div> : null}
        </Card>
      </div>
      <h2 className="sec-h">{L('Looks unusual', 'غير معتاد')}</h2>
      {r.flags.length ? r.flags.map(x => { const [sl, st] = SEV()[x.severity]; return (
        <div key={x.id} className="card an" id={'an-' + x.id}>
          <div className="an-h"><Pill tone={st}>{sl}</Pill><b>{ar() ? x.title_ar : x.title_en}</b></div>
          <p className="muted sm">{ar() ? x.why_ar : x.why_en}</p>
          <div className="an-act">
            <button type="button" className="link" onClick={() => show(x.filter)}>{L('Show the entries', 'اعرض السجلات')}</button>
            {x.action ? <Btn kind="ghost" sm icon="spark" disabled={!r.can_approve} why={L('Only a super admin can approve what the AI suggests.', 'فقط المسؤول الأعلى يعتمد اقتراح الذكاء.')} onClick={() => approve(x)}>{ar() ? x.action.label_ar : x.action.label_en}</Btn> : null}
            <button type="button" className="link muted" onClick={() => void dismiss(x)}>{L('Dismiss', 'تجاهل')}</button>
          </div>
        </div>
      ); }) : <Card><Empty icon="check" title={L('Nothing unusual right now', 'لا شيء غير معتاد')} /></Card>}
      <p className="muted sm lg-ret"><Ic n="lock" cls="sm" /> {L('The AI only reads the log; it never changes anything. A suggestion runs only when a super admin confirms it, and is logged as "AI suggested, name approved".', 'الذكاء يقرأ السجل فقط ولا يغير شيئاً. لا يُنفذ اقتراح إلا بتأكيد مسؤول أعلى ويُسجل كذلك.')}</p>
    </>
  );
}
